import { inject, Injectable, OnDestroy } from '@angular/core';
import type { Faro } from '@grafana/faro-web-sdk';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { APP_CONFIG } from 'src/modules/main/providers/app.config';

import { CookieConsentService } from '../analytics/cookie-consent/cookie-consent.service';
import { UserConsentValue } from '../analytics/cookie-consent/types';
import { FARO } from './faro.token';
import { setRumConsent } from './initialize-monitoring';

/**
 * Grafana Faro frontend monitoring. Errors and web vitals are always
 * collected; behavioral RUM signals are consent-gated (see
 * initialize-monitoring.ts). All methods no-op when monitoring is disabled
 * for the current environment or Faro failed to initialize.
 */
@Injectable({
    providedIn: 'root',
})
export class MonitoringService implements OnDestroy {
    private readonly faro: Faro = inject(FARO);
    private readonly available: boolean;
    private readonly userConsentSubscription: Subscription | undefined;

    constructor(private cookieConsentService: CookieConsentService) {
        const appConfig = inject(APP_CONFIG);

        this.available = appConfig.MONITORING?.enabled === true && Boolean(this.faro?.api);

        if (this.available) {
            this.userConsentSubscription = this.cookieConsentService.userConsent$
                .pipe(debounceTime(125))
                .subscribe(value => {
                    this.onUserConsent(value);
                });
        }
    }

    ngOnDestroy(): void {
        this.userConsentSubscription?.unsubscribe();
    }

    trackError(error: unknown, context?: Record<string, string>): void {
        this.delegate(() => {
            this.faro.api.pushError(MonitoringService.coerceToError(error), { context });
        });
    }

    trackAction(name: string, attributes?: Record<string, string>): void {
        this.delegate(() => {
            this.faro.api.pushEvent(name, attributes);
        });
    }

    trackMeasurement(type: string, values: Record<string, number>): void {
        this.delegate(() => {
            this.faro.api.pushMeasurement({ type, values });
        });
    }

    setUser(userId: string | null | undefined, companyAccountId?: string | null): void {
        if (!userId) return;

        this.delegate(() => {
            this.faro.api.setUser({
                id: userId,
                attributes: companyAccountId ? { company_account_id: companyAccountId } : undefined,
            });
        });
    }

    resetUser(): void {
        this.delegate(() => {
            this.faro.api.resetUser();
        });
    }

    private delegate(fn: () => void): void {
        if (this.available) {
            fn();
        }
    }

    private onUserConsent(current: UserConsentValue): void {
        const granted = current.analytics === true && current.services.analytics.includes('faro');
        setRumConsent(granted);
    }

    private static coerceToError(error: unknown): Error {
        if (error instanceof Error) {
            return error;
        }
        return new Error(typeof error === 'string' ? error : JSON.stringify(error));
    }
}
